import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, ArrowLeft, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Seo from "../components/Seo";

export default function MemberSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus-visible:ring-2 focus-visible:ring-electric/40 transition";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      await api.post("/members/signup", payload);
      setDone(true);
      toast.success("Check your email to verify your account.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main id="main-content" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-20" data-testid="signup-success">
        <Seo title="Check Your Email" path="/signup" />
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto mb-6 ring-1 ring-electric/40">
            <Mail className="w-10 h-10 text-electric" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">Check your email</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-4">
            One more <span className="italic text-electric">step.</span>
          </h1>
          <p className="text-zinc-300 leading-relaxed mb-8">
            We sent a verification link to <strong className="text-white">{form.email}</strong>. Click it to activate your account, then log in to find your groups.
          </p>
          <p className="text-xs text-zinc-500 mb-6">
            Didn't get the email?{" "}
            <button
              onClick={async () => {
                try {
                  await api.post("/members/resend-verification", { email: form.email });
                  toast.success("Verification email re-sent.");
                } catch { toast.error("Could not resend"); }
              }}
              className="text-electric font-bold underline"
              data-testid="resend-verification"
            >
              Resend
            </button>
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-electric text-sm" data-testid="signup-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-20 relative overflow-hidden" data-testid="signup-page">
      <Seo title="Sign Up" description="Join The Push Center member portal — connect with your groups, receive announcements, song lists, and event info." path="/signup" />
      <div className="absolute inset-0 lightning-grid opacity-30" />
      <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-electric/10 rounded-full blur-3xl" />

      <Link to="/" className="absolute top-6 left-6 text-zinc-400 hover:text-electric flex items-center gap-2 text-sm" data-testid="signup-back">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </Link>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="The Push Center" className="w-20 h-20 rounded-full ring-2 ring-electric/40 mb-5" />
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-2">
            Join the Family
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Create your account.</h1>
        </div>

        <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 space-y-5" data-testid="signup-form">
          <Field label="Full Name">
            <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" />
          </Field>
          <Field label="Email">
            <input type="email" className={inputCls} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" />
          </Field>
          <Field label="Phone (optional)">
            <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="signup-phone" />
          </Field>
          <Field label="Password (min 8 characters)">
            <input type="password" minLength={8} className={inputCls} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password" />
          </Field>

          <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-electric text-black px-7 py-4 rounded-lg font-bold tracking-wide hover:bg-[#4ade80] transition-all duration-300 disabled:opacity-60" data-testid="signup-submit">
            <UserPlus className="w-4 h-4" />
            {submitting ? "Creating account..." : "Create Account"}
          </button>

          <p className="text-center text-sm text-zinc-400">
            Already have an account?{" "}
            <Link to="/login" className="text-electric font-bold hover:underline" data-testid="signup-to-login">Sign in</Link>
          </p>
        </form>
      </motion.div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">{label}</label>
      {children}
    </div>
  );
}
